import { Link } from 'react-router-dom';
import styles from './Privacy.module.css';

export default function Privacy() {
  return (
    <div className={styles.page}>
      <div className={styles.container}>
        <Link to="/" className={styles.backLink}>&larr; Back to Vennio</Link>

        <h1 className={styles.title}>Privacy Policy</h1>
        <p className={styles.date}>Last updated: 5 May 2026</p>

        <div className={styles.prose}>

          <section>
            <h2>1. Introduction</h2>
            <p>
              Vennio ("we," "us," or "our") is a demand-side scheduling infrastructure platform that helps
              businesses and individuals coordinate meetings by aggregating calendar availability. This Privacy
              Policy explains how we collect, use, store, and protect your personal information when you use
              our service at vennio.app.
            </p>
            <p>
              We are committed to protecting your privacy and handling your data transparently. Please read
              this policy carefully to understand our practices regarding your personal data.
            </p>
          </section>

          <section>
            <h2>2. Data Controller</h2>
            <p>For the purposes of UK GDPR and EU GDPR, the data controller is:</p>
            <p><strong>Whisker One Ltd</strong> (trading as Vennio)</p>
            <p>Company number: 16270781</p>
            <p>Registered office: 34 The Broadway, North Shields NE30 2LQ</p>
            <p>Email: <a href="mailto:matt@vennio.app">matt@vennio.app</a></p>
            <p>Website: <a href="https://vennio.app">https://vennio.app</a></p>
          </section>

          <section>
            <h2>3. Information We Collect</h2>

            <h3>3.1 Account Information</h3>
            <p>When you create an account, we collect:</p>
            <ul>
              <li><strong>Name</strong>: Your full name as provided by your authentication provider</li>
              <li><strong>Email address</strong>: Used for account identification and communications</li>
              <li><strong>Profile picture</strong>: If provided by your authentication provider (optional)</li>
              <li><strong>Authentication tokens</strong>: Encrypted OAuth tokens to maintain your calendar connections</li>
            </ul>
            <p>You may authenticate using Google OAuth or Microsoft OAuth.</p>

            <h3>3.2 Calendar Data</h3>
            <p>With your explicit permission, we access calendar data from your connected accounts.</p>
            <p><strong>What we read and store:</strong></p>
            <ul>
              <li>Free/busy status (available, busy, tentative)</li>
              <li>Event start and end times</li>
              <li>Calendar timezone settings</li>
              <li>The list of calendars in your connected account (so you can choose which calendar Vennio uses)</li>
            </ul>
            <p><strong>What we do not read or store:</strong></p>
            <ul>
              <li>Event titles or descriptions</li>
              <li>Attendee lists or email addresses</li>
              <li>Event locations</li>
              <li>Meeting notes or attachments</li>
              <li>Private or sensitive event details</li>
            </ul>
            <p>
              The OAuth scopes we request technically permit access to event titles, attendees, descriptions, and locations.
              In line with Google's Limited Use requirements and our own data minimisation principles, Vennio reads and stores
              only the timing and free/busy fields listed above. We do not read, store, log, or transmit event content fields,
              even when the API returns them.
            </p>
            <p>
              The only exception is when Vennio creates a calendar event on your behalf in response to a confirmed booking.
              In that case, we write a title (e.g. "Meeting with [customer name]"), the booking time, and the customer's email
              address as an attendee. We do not modify any of your existing events.
            </p>
            <p>
              If additional calendar permissions are required for specific features in future, we will clearly explain
              what data is needed and request your explicit consent before accessing it.
            </p>

            <h3>3.3 Scheduling Data</h3>
            <p>When you use our scheduling features, we collect:</p>
            <ul>
              <li>Proposed meeting times and responses</li>
              <li>Scheduling preferences and settings</li>
              <li>Booking confirmations and history</li>
              <li>Communications related to scheduling</li>
            </ul>

            <h3>3.4 Usage Data</h3>
            <p>We automatically collect certain information when you use the Service:</p>
            <ul>
              <li>Pages and features accessed</li>
              <li>Time spent on the Service</li>
              <li>Actions taken (clicks, form submissions)</li>
              <li>Device information (browser type, operating system)</li>
              <li>IP address (anonymised for analytics)</li>
              <li>Referring website or source</li>
            </ul>

            <h3>3.5 Communications Data</h3>
            <p>When you contact us, we collect:</p>
            <ul>
              <li>Email correspondence</li>
              <li>Support requests and feedback</li>
              <li>Any information you choose to provide</li>
            </ul>
          </section>

          <section>
            <h2>4. How We Use Your Information</h2>
            <p>We use your information for the following purposes and legal bases:</p>

            <div className={styles.tableWrap}>
              <table>
                <thead>
                  <tr>
                    <th>Purpose</th>
                    <th>Legal Basis (UK/EU GDPR)</th>
                  </tr>
                </thead>
                <tbody>
                  <tr><td>Provide scheduling and calendar coordination services</td><td>Performance of contract</td></tr>
                  <tr><td>Display your availability to parties you authorise</td><td>Performance of contract</td></tr>
                  <tr><td>Send booking confirmations, reminders, and service notifications</td><td>Performance of contract</td></tr>
                  <tr><td>Maintain and secure your account</td><td>Performance of contract / Legitimate interests</td></tr>
                  <tr><td>Process payments and manage subscriptions</td><td>Performance of contract</td></tr>
                  <tr><td>Respond to your enquiries and support requests</td><td>Performance of contract / Legitimate interests</td></tr>
                  <tr><td>Improve and develop new features</td><td>Legitimate interests</td></tr>
                  <tr><td>Analyse usage patterns and service performance</td><td>Legitimate interests</td></tr>
                  <tr><td>Send product updates and announcements</td><td>Legitimate interests (with opt-out)</td></tr>
                  <tr><td>Comply with legal obligations</td><td>Legal obligation</td></tr>
                  <tr><td>Protect against fraud and abuse</td><td>Legitimate interests</td></tr>
                </tbody>
              </table>
            </div>

            <p>We will never use your calendar data to:</p>
            <ul>
              <li>Serve you advertisements</li>
              <li>Build advertising profiles</li>
              <li>Sell to third parties</li>
              <li>Train machine learning models unrelated to providing you the Service</li>
            </ul>
          </section>

          <section>
            <h2>5. Google API Services Disclosure</h2>
            <p>
              Vennio's use and transfer to any other app of information received from Google APIs will adhere to the{' '}
              <a href="https://developers.google.com/terms/api-services-user-data-policy" target="_blank" rel="noopener noreferrer">
                Google API Services User Data Policy
              </a>, including the Limited Use requirements.
            </p>
            <p>We request the following Google OAuth scopes:</p>
            <ul>
              <li>
                <strong>https://www.googleapis.com/auth/calendar.events</strong> — used to read event start and end times for
                availability calculation, to create calendar events when bookings are confirmed (with the booker added as an
                attendee), and to register webhooks that notify Vennio of changes to your calendar so availability stays
                accurate. We do not read or store event titles, descriptions, attendees of your existing events, or event locations.
              </li>
              <li>
                <strong>https://www.googleapis.com/auth/calendar.calendarlist.readonly</strong> — used to display the list of
                your calendars so you can choose which one Vennio reads availability from and writes booking events to.
              </li>
              <li>
                <strong>openid</strong>, <strong>email</strong>, <strong>profile</strong> — used to identify your Vennio account
                and display your name and email in the application.
              </li>
            </ul>
            <p>
              We do not request access to your full Google Calendar, the ability to share or modify calendars themselves, or any
              scope outside the list above.
            </p>
            <p>Specifically, we confirm that:</p>
            <ol>
              <li>
                <strong>Limited Use</strong>: We only use Google user data to provide and improve the scheduling
                features you have requested. We do not use Google user data to serve advertisements, build advertising profiles,
                or train machine learning models unrelated to providing the Service.
              </li>
              <li>
                <strong>No unauthorised sharing</strong>: We do not transfer Google user data to third parties except:
                <ul>
                  <li>As necessary to provide the Service (for example, infrastructure providers listed in Section 7.2)</li>
                  <li>With your explicit consent</li>
                  <li>As required by law</li>
                  <li>In connection with a merger, acquisition, or sale of assets, with notice to you</li>
                </ul>
              </li>
              <li>
                <strong>No human access without consent</strong>: We do not allow humans to read your Google user data unless:
                <ul>
                  <li>You have given us affirmative agreement for specific messages (for example, when you submit a support request that references your calendar)</li>
                  <li>It is necessary for security purposes (for example, investigating abuse or a security incident)</li>
                  <li>It is required to comply with applicable law</li>
                  <li>The data has been aggregated and anonymised, and is being used for internal operations</li>
                </ul>
              </li>
              <li>
                <strong>Security</strong>: We implement appropriate security measures to protect Google user data from
                unauthorised access, alteration, disclosure, or destruction. OAuth tokens are encrypted at rest using AES-256-GCM.
                Access controls and authentication apply to all systems handling Google user data.
              </li>
            </ol>
          </section>

          <section>
            <h2>6. Microsoft API Services Disclosure</h2>
            <p>
              When you connect a Microsoft account, we access your calendar data through the Microsoft Graph API.
              We request the <strong>Calendars.ReadWrite</strong> scope to read your calendar events for availability calculation and create events when bookings are confirmed.
              We comply with Microsoft's API Terms of Use and the Microsoft Privacy Statement.
            </p>
            <p>
              We apply the same data minimisation and security principles to Microsoft data as we do to Google data,
              as described in this Privacy Policy.
            </p>
          </section>

          <section>
            <h2>7. Data Sharing</h2>
            <p>We do not sell, rent, or trade your personal information.</p>
            <p>We share your data only in the following circumstances:</p>

            <h3>7.1 With Your Consent</h3>
            <ul>
              <li><strong>Availability sharing</strong>: We display your free/busy times to parties you explicitly choose to share with through the Service</li>
              <li><strong>Integration partners</strong>: When you connect third-party services (HubSpot, Salesforce, etc.), we share necessary data to enable the integration</li>
            </ul>

            <h3>7.2 Service Providers</h3>
            <p>We use trusted third-party service providers to help operate the Service:</p>

            <div className={styles.tableWrap}>
              <table>
                <thead>
                  <tr>
                    <th>Provider</th>
                    <th>Purpose</th>
                    <th>Data Shared</th>
                    <th>Location</th>
                  </tr>
                </thead>
                <tbody>
                  <tr><td>Google Cloud / Google Calendar API</td><td>Calendar integration</td><td>Calendar data, OAuth tokens</td><td>USA/Global</td></tr>
                  <tr><td>Microsoft Graph API</td><td>Calendar integration</td><td>Calendar data, OAuth tokens</td><td>USA/Global</td></tr>
                  <tr><td>Supabase</td><td>Database and authentication</td><td>Account data, application data</td><td>USA (AWS)</td></tr>
                  <tr><td>Vercel</td><td>Hosting and serverless functions</td><td>Usage data, application data</td><td>USA/Global</td></tr>
                  <tr><td>Resend</td><td>Transactional email delivery</td><td>Email address, name</td><td>USA</td></tr>
                  <tr><td>HubSpot (optional)</td><td>CRM integration</td><td>Contact data, scheduling data</td><td>USA</td></tr>
                  <tr><td>Salesforce (optional)</td><td>CRM integration</td><td>Contact data, scheduling data</td><td>USA</td></tr>
                </tbody>
              </table>
            </div>

            <p>All service providers are contractually obligated to protect your data and use it only for the purposes we specify.</p>

            <h3>7.3 Legal Requirements</h3>
            <p>We may disclose your information if required to do so by law or in response to valid requests by public authorities (e.g., court order, government request).</p>

            <h3>7.4 Business Transfers</h3>
            <p>If Vennio is involved in a merger, acquisition, or sale of assets, your information may be transferred. We will provide notice before your data is transferred and becomes subject to a different privacy policy.</p>

            <h3>7.5 Protection of Rights</h3>
            <p>We may disclose information to protect the rights, property, or safety of Vennio, our users, or others.</p>
          </section>

          <section>
            <h2>8. International Data Transfers</h2>
            <p>Your information may be transferred to and processed in countries outside the United Kingdom and European Economic Area, including the United States, where our service providers are located.</p>
            <p>When we transfer data internationally, we ensure appropriate safeguards are in place:</p>
            <ul>
              <li><strong>Standard Contractual Clauses (SCCs)</strong>: We use EU/UK-approved standard contractual clauses with our service providers</li>
              <li><strong>Adequacy decisions</strong>: Where applicable, we rely on adequacy decisions by the UK or EU</li>
              <li><strong>Supplementary measures</strong>: We implement additional technical and organisational measures as needed</li>
            </ul>
            <p>You can request more information about these safeguards by contacting us at <a href="mailto:matt@vennio.app">matt@vennio.app</a>.</p>
          </section>

          <section>
            <h2>9. Data Retention</h2>
            <p>We retain your data for as long as necessary to provide the Service and fulfil the purposes described in this policy:</p>

            <div className={styles.tableWrap}>
              <table>
                <thead>
                  <tr>
                    <th>Data Type</th>
                    <th>Retention Period</th>
                  </tr>
                </thead>
                <tbody>
                  <tr><td>Account information</td><td>Until you delete your account, plus 30 days</td></tr>
                  <tr><td>Calendar data (cached)</td><td>Real-time sync; deleted within 30 days of disconnection</td></tr>
                  <tr><td>OAuth tokens</td><td>Until you disconnect the calendar or delete your account</td></tr>
                  <tr><td>Scheduling history</td><td>2 years from the scheduled event date</td></tr>
                  <tr><td>Usage analytics</td><td>26 months (anonymised/aggregated)</td></tr>
                  <tr><td>Support correspondence</td><td>3 years from last contact</td></tr>
                  <tr><td>Payment records</td><td>7 years (legal requirement)</td></tr>
                  <tr><td>Backup copies</td><td>Up to 90 days after deletion from active systems</td></tr>
                </tbody>
              </table>
            </div>

            <p>When you delete your account:</p>
            <ul>
              <li>Your personal data is deleted from active systems within 30 days</li>
              <li>Backup copies are purged within 90 days</li>
              <li>Anonymised, aggregated data may be retained indefinitely</li>
            </ul>
          </section>

          <section>
            <h2>10. Data Security</h2>
            <p>We implement appropriate technical and organisational measures to protect your data:</p>
            <p><strong>Technical measures:</strong></p>
            <ul>
              <li>Encryption in transit (TLS/HTTPS)</li>
              <li>Encryption at rest for sensitive data</li>
              <li>Secure OAuth token storage (encrypted, not plain text)</li>
              <li>Regular security assessments</li>
              <li>Access controls and authentication</li>
              <li>Automated vulnerability scanning</li>
            </ul>
            <p><strong>Organisational measures:</strong></p>
            <ul>
              <li>Limited access to personal data on a need-to-know basis</li>
              <li>Security training and awareness</li>
              <li>Incident response procedures</li>
              <li>Regular review of security practices</li>
            </ul>
            <p>While we strive to protect your data, no method of transmission or storage is 100% secure. If you become aware of any security issues, please contact us immediately at <a href="mailto:matt@vennio.app">matt@vennio.app</a>.</p>
          </section>

          <section>
            <h2>11. Data Breach Notification</h2>
            <p>In the event of a personal data breach that is likely to result in a risk to your rights and freedoms, we will:</p>
            <ol>
              <li>Notify the Information Commissioner's Office (ICO) within 72 hours of becoming aware of the breach</li>
              <li>Notify affected users without undue delay if the breach is likely to result in a high risk to your rights and freedoms</li>
              <li>Document the breach and remediation steps taken</li>
            </ol>
          </section>

          <section>
            <h2>12. Your Rights</h2>
            <p>Under UK GDPR and EU GDPR, you have the following rights regarding your personal data:</p>

            <h3>12.1 Right of Access</h3>
            <p>You can request a copy of the personal data we hold about you.</p>

            <h3>12.2 Right to Rectification</h3>
            <p>You can request that we correct inaccurate or incomplete data.</p>

            <h3>12.3 Right to Erasure ("Right to be Forgotten")</h3>
            <p>You can request that we delete your personal data in certain circumstances, such as when the data is no longer necessary for the purposes for which it was collected.</p>

            <h3>12.4 Right to Restrict Processing</h3>
            <p>You can request that we limit how we use your data in certain circumstances.</p>

            <h3>12.5 Right to Data Portability</h3>
            <p>You can request your data in a structured, commonly used, machine-readable format and have it transferred to another controller.</p>

            <h3>12.6 Right to Object</h3>
            <p>You can object to processing based on legitimate interests. We will stop processing unless we have compelling legitimate grounds.</p>

            <h3>12.7 Rights Related to Automated Decision-Making</h3>
            <p>We do not currently make decisions based solely on automated processing that produce legal or similarly significant effects.</p>

            <h3>12.8 Right to Withdraw Consent</h3>
            <p>Where processing is based on consent, you can withdraw consent at any time without affecting the lawfulness of processing before withdrawal.</p>

            <p><strong>To exercise your rights:</strong></p>
            <ul>
              <li>Email us at <a href="mailto:matt@vennio.app">matt@vennio.app</a></li>
              <li>Use the self-service options in your account settings (where available)</li>
            </ul>

            <p>We will respond to your request within one month. This period may be extended by two further months for complex requests, in which case we will inform you.</p>
            <p>We may ask you to verify your identity before processing your request.</p>

            <h3>12.9 Right to Complain</h3>
            <p>If you believe your data protection rights have been violated, you have the right to lodge a complaint with a supervisory authority. In the UK, this is:</p>
            <p><strong>Information Commissioner's Office (ICO)</strong></p>
            <p>Website: <a href="https://ico.org.uk" target="_blank" rel="noopener noreferrer">https://ico.org.uk</a></p>
            <p>Helpline: 0303 123 1113</p>
          </section>

          <section>
            <h2>13. Children's Privacy</h2>
            <p>The Service is not intended for children under 16 years of age. We do not knowingly collect personal information from children under 16.</p>
            <p>If you are a parent or guardian and believe your child has provided us with personal information, please contact us at <a href="mailto:matt@vennio.app">matt@vennio.app</a>. If we discover that we have collected personal information from a child under 16, we will delete that information promptly.</p>
          </section>

          <section>
            <h2>14. Cookies and Similar Technologies</h2>

            <h3>14.1 What We Use</h3>
            <p>We use cookies and similar technologies for the following purposes:</p>

            <div className={styles.tableWrap}>
              <table>
                <thead>
                  <tr>
                    <th>Type</th>
                    <th>Purpose</th>
                    <th>Duration</th>
                  </tr>
                </thead>
                <tbody>
                  <tr><td>Essential cookies</td><td>Session management, authentication, security</td><td>Session / 30 days</td></tr>
                  <tr><td>Preference cookies</td><td>Remember your settings and choices</td><td>1 year</td></tr>
                  <tr><td>Analytics cookies</td><td>Understand how users interact with the Service</td><td>26 months</td></tr>
                </tbody>
              </table>
            </div>

            <h3>14.2 What We Don't Use</h3>
            <p>We do not use:</p>
            <ul>
              <li>Third-party advertising cookies</li>
              <li>Cross-site tracking cookies</li>
              <li>Social media tracking pixels</li>
            </ul>

            <h3>14.3 Managing Cookies</h3>
            <p>You can control cookies through your browser settings. Note that disabling essential cookies may prevent you from using certain features of the Service.</p>
          </section>

          <section>
            <h2>15. Third-Party Links</h2>
            <p>The Service may contain links to third-party websites or services. We are not responsible for the privacy practices of these third parties. We encourage you to read the privacy policies of any third-party services you access.</p>
          </section>

          <section>
            <h2>16. Changes to This Policy</h2>
            <p>We may update this Privacy Policy from time to time to reflect changes in our practices or for legal, operational, or regulatory reasons.</p>
            <p><strong>How we will notify you:</strong></p>
            <ul>
              <li>Material changes: Email notification and prominent notice on the Service</li>
              <li>Minor changes: Updated "Last updated" date on this page</li>
            </ul>
            <p>We encourage you to review this policy periodically. Your continued use of the Service after changes become effective constitutes acceptance of the revised policy.</p>
          </section>

          <section>
            <h2>17. Contact Us</h2>
            <p>If you have any questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us:</p>
            <p><strong>Email</strong>: <a href="mailto:matt@vennio.app">matt@vennio.app</a></p>
            <p><strong>Website</strong>: <a href="https://vennio.app">https://vennio.app</a></p>
            <p>We aim to respond to all enquiries within 5 business days.</p>
          </section>

          <hr />

          <p className={styles.closing}>By using Vennio, you acknowledge that you have read and understood this Privacy Policy.</p>

        </div>
      </div>
    </div>
  );
}
